import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import { addMinutes, differenceInMinutes } from "date-fns";

import GlobalConfig from "app/config/global";
import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import { useLocation } from "react-router";
import { useLocationState } from "react-router-use-location-state";
import { useParams } from "react-router-dom";
import useProjectsDashboardNotes from "features/projects/project-dashboard/single/useProjectsDashboardNotes";

export const TIME_INTERVAL_IN_MIN = 5;

function reducer(oldState, action) {
    const { projectID, dateTime, notes } = action.payload;
    let projectsAndNotesData = [];
    oldState.map((d) => projectsAndNotesData.push(d));

    const setProjectsNotesIcon = (projectIdIndex = null) => {
        let newNotesData = [...notes];
        const projectLastNoteVisitedLocalDateTime = projectIdIndex
            ? projectsAndNotesData[projectIdIndex]?.dateTime
            : dateTime;

        newNotesData.forEach((d, i) => {
            const dateDiffInMin = differenceInMinutes(
                addMinutes(
                    projectLastNoteVisitedLocalDateTime,
                    new Date(d.Date).getTimezoneOffset()
                    /* Adding timezone offset here as local and server time is different */
                ),
                new Date(d.Date)
                /* Server time when note has been created */
            );

            d.isNoteVisited =
                projectsAndNotesData[projectIdIndex]?.notesData[
                    i
                ]?.isNoteVisited;

            d.showNotificationIcon =
                dateDiffInMin < TIME_INTERVAL_IN_MIN &&
                dateDiffInMin >= 0 &&
                !projectsAndNotesData[projectIdIndex]?.notesData[i]
                    ?.isNoteVisited;
        });

        return newNotesData;
    };

    if (action.type === "setProjectNoteData") {
        const projectIdIndex = projectsAndNotesData.findIndex(
            (f) => f.id === projectID
        );
        if (projectIdIndex === -1) {
            const newNotesData = setProjectsNotesIcon();

            projectsAndNotesData.push({
                id: projectID,
                projectDateTime: dateTime,
                notesData: setProjectsNotesIcon(),
                showNotificationIconForNotesTab: newNotesData?.some(
                    (s) => s.showNotificationIcon
                ),
                isNoteVisited: false,
            });
        } else if (projectIdIndex > -1) {
            projectsAndNotesData[projectIdIndex].id = projectID;
            projectsAndNotesData[projectIdIndex].projectDateTime = dateTime;

            const newNotesData = setProjectsNotesIcon(projectIdIndex);

            projectsAndNotesData[projectIdIndex].notesData = newNotesData;

            projectsAndNotesData[
                projectIdIndex
            ].showNotificationIconForNotesTab = newNotesData?.some(
                (s) => s.showNotificationIcon
            );
        }
        return projectsAndNotesData;
    } else if (action.type === "setAllNotesDataIconToFalse") {
        const projectIdIndex = projectsAndNotesData.findIndex(
            (f) => f.id === projectID
        );
        projectsAndNotesData[projectIdIndex].notesData.forEach((d) => {
            d.showNotificationIcon = false;
            d.isNoteVisited = true;
        });
        projectsAndNotesData[
            projectIdIndex
        ].showNotificationIconForNotesTab = false;

        return projectsAndNotesData;
    } else {
        throw new Error("Invalid Action Type");
    }
}

const ProjectsDashboardLayoutContext = React.createContext({
    setProjectNoteVistitedDateTime: () => void 0,
    setAllNotesDataIconToFalse: () => void 0,
});

export const ProjectsDashboardLayoutContextProvider = (props) => {
    const { state } = useLocation();

    const { projectId } = useParams();
    const sessionData = useContext(SessionContext);
    const location = useLocation();

    const [filters, setFilters] = useState();
    const cuurentProjectId = useRef(projectId);

    // To reset note type filter when retainer selector changes
    useEffect(() => {
        setFilters([]);
        cuurentProjectId.current = projectId;
    }, [projectId]);

    const filterVal = filters?.find((f) => f.id === "TypeId")?.value ?? "";
    const noteTypeIdVal =
        cuurentProjectId.current === projectId ? filterVal : "";

    const { useProjectsNotesList } = useProjectsDashboardNotes();
    const notesList = useProjectsNotesList({
        projectIdFE: projectId,
        noteTypeId: noteTypeIdVal,
    });

    const pathNameIncludesNotes = location.pathname.includes(
        `/${GlobalConfig.routes.projectsProjectsDashboardNotesTab}`
    );

    const [initialState] = useState(() => {
        const initialStateData = [];
        let stateType = Array.isArray(state);
        if (state?.length !== 0 && stateType) {
            state?.forEach((data) => {
                initialStateData.push(data.key);
            });
        }
        return initialStateData;
    });

    const [projectNoteTabVisitState, setProjectNoteTabVisitState] =
        useLocationState("projectsAndNotesState", initialState);

    const [newReducerState, dispatch] = useReducer(
        reducer,
        projectNoteTabVisitState
    );

    const [flagToLoad, setFlagToLoad] = useState(true);

    const setProjectNoteData = useCallback((projectID, dateTime, notes) => {
        dispatch({
            type: "setProjectNoteData",
            payload: { projectID, dateTime, notes },
        });
    }, []);

    const setAllNotesDataIconToFalse = useCallback((projectID) => {
        dispatch({
            type: "setAllNotesDataIconToFalse",
            payload: { projectID },
        });
    }, []);

    const getNotesTabIndicator = useCallback(
        (projectID) =>
            newReducerState.find((f) => f.id === projectID)
                ?.showNotificationIconForNotesTab,
        [newReducerState]
    );

    const getNotesData = useCallback(
        (projectID) =>
            newReducerState.find((f) => f.id === projectID)?.notesData,
        [newReducerState]
    );

    const checkIfNotesDataContainsIcon = useCallback(
        (projectID) =>
            newReducerState
                .find((f) => f.id === projectID)
                ?.notesData?.some((s) => s.showNotificationIcon),
        [newReducerState]
    );

    const [updatedNotesData, setUpdatedNotesData] = useState(
        getNotesData(projectId) ?? []
    );
    
    const prevNotesListRef = useRef();
    
    useEffect(() => {
        const notesD = getNotesData(projectId);
        setUpdatedNotesData(notesD);
    }, [projectId, newReducerState, getNotesData]); 

    useEffect(() => {
        if (!notesList.isFetching && notesList.data) {
            // Evitar actualizaciones si los datos no han cambiado
            if (prevNotesListRef.current === notesList.data) {
                return;
            }
            
            prevNotesListRef.current = notesList.data;
            
            // To display edit/view icon require isCreatorOfNote
            notesList.data.forEach((d) => {
                d.isCreatorOfNote =
                    d.Creator === sessionData.fullName;
            });

            // Calcular valores directamente del state
            const projectData = newReducerState.find((f) => f.id === projectId);
            const noteVisitedDateTime = projectData?.value;
            const currentNotesListLength = projectData?.notesData?.length;

            // To store projects related data
            const dateDiffInMin = differenceInMinutes(
                new Date(),
                noteVisitedDateTime
            );

            if (
                notesList.data.length >= 0 &&
                notesList.data.length !== currentNotesListLength
            ) {
                setFlagToLoad(true);
            }

            if (Number.isNaN(dateDiffInMin) && flagToLoad) {
                setFlagToLoad(false);
                setProjectNoteData(
                    projectId,
                    noteVisitedDateTime ?? new Date().toISOString(),
                    notesList.data
                );
            }
        }
    }, [notesList.isFetching, notesList.data, flagToLoad, projectId, sessionData.fullName, setProjectNoteData, newReducerState]); 

    // When filter selected; to update notes data updating flag state here
    useEffect(() => {
        if (filters?.length > 0 && !flagToLoad) {
            setFlagToLoad(true);
        }
    }, [filters, flagToLoad]);

    // Remove indicator from table row
    const hasNotesDataIcon = checkIfNotesDataContainsIcon(projectId);
    useEffect(() => {
        let timer = null;
        if (hasNotesDataIcon && pathNameIncludesNotes) {
            timer = setTimeout(() => {
                setAllNotesDataIconToFalse(projectId);
            }, 30000); // 30000ms i.e. 30s
        }

        return () => {
            clearTimeout(timer);
        };
    }, [hasNotesDataIcon, pathNameIncludesNotes, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setProjectNoteTabVisitState(newReducerState);
    }, [newReducerState, setProjectNoteTabVisitState]);

    const contextValue = useMemo(() => {
        return {
            getNotesTabIndicator,
            updatedNotesData,
            newReducerState,
            notesList,
            setFilters,
            setFlagToLoad,
        };
    }, [
        getNotesTabIndicator,
        updatedNotesData,
        newReducerState,
        notesList,
        setFilters,
        setFlagToLoad,
    ]);

    return (
        <ProjectsDashboardLayoutContext.Provider value={contextValue}>
            {props.children}
        </ProjectsDashboardLayoutContext.Provider>
    );
};

ProjectsDashboardLayoutContextProvider.propTypes = {
    children: PropTypes.node
};

export default ProjectsDashboardLayoutContext;
